import { getWallPath } from "building-generation/elements/walls/wall";
import type { Block } from "building-types/blocks/blockTypes";
import type { Building } from "building-types/building/buildingTypes";
import { getSourceRoomId } from "building-types/components/componentInstanceUtils";
import type { GeneratedDoorPlacement } from "building-types/doors/doorTypes";
import type { Level } from "building-types/levels/levelTypes";
import type { WallOpening } from "building-types/openings/wallOpeningTypes";
import type { LevelHalfEdgeAssociationTable } from "building-types/planarGraphs/planarGraphAssociationTypes";
import type { TargetedPorchSettings } from "building-types/porches/porch";
import { RoomType } from "building-types/rooms/roomTypesEnum";
import type { PorchSettings } from "building-types/settings/porchSettings";
import type { RearPorchSettings } from "building-types/settings/rearPorchSettings";
import type { RoofSystemLevel } from "building-types/settings/roofTypes";
import type { WallCut } from "building-types/walls/wallCutTypes";
import { WallType } from "building-types/walls/wallEnums";
import type { GeneratedWindowPlacement } from "building-types/windows/windowTypes";
import type { RWTable, Table } from "common/data/table";
import { assertIsNotNullOrUndefined } from "common/utils/assertOfType";
import { assertUnreachable } from "common/utils/assertUnreachable";
import { isInteriorRoomEnclosure } from "doc/rooms/roomAttributesTools";
import { MULTIGABLE_BSP_EPSILON } from "doc/utils/roofTools";
import { isExteriorWallCondition } from "doc/utils/wallTypeTools";
import type { Face } from "geom/brep";
import { faceNew, faceOrNullReverse, mergeNonAdjacentCoplanarFaces } from "geom/brep";
import {
  addPolygonsToBSP,
  buildEdgePolygon,
  buildPolygonsFromFaces,
  buildPolygonsFromPolyline2,
  isValidFaceForBSPVolume,
  polygonBSPNew,
} from "geom/bsp";
import type { ClipFaceContext, ClippingNode } from "geom/bspClipping";
import { clipFaces, clippingNodeNew } from "geom/bspClipping";
import type { NodeBSP, PolygonBSP } from "geom/bspTypes";
import { gatherOrientedCoplanarFaceGroupsFast } from "geom/coplanarFaceGroups";
import { DIST_EPSILON, DIST_EPSILON_SQUARED, ONE_MINUS_DIST_EPSILON } from "geom/core";
import { offsetPolyLine2, offsetPolyLine2Variable } from "geom/offset";
import {
  planeCopyReversed,
  planeMoveAlongNormal,
  planeNewXY,
  planeNormal,
  planeReProjectPolyLine2,
  polyLine2OnPlane,
} from "geom/plane";
import { planeBSPByThreePoints } from "geom/planeBSP";
import {
  polyLine2Area,
  polyLine2ByTwoPoints,
  polyLine2Concat,
  polyLine2Copy,
  polyLine2RemoveInvalidVertices,
  polyLine2Reverse,
  polyLine2SetCW,
  polyLine2Simplify,
} from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { vec3Dot } from "geom/vec3";
import { solidWallSubstrateLayer } from "../walls/wallLayers";

export function getUpperFloorsRoomsBSPs(
  levels: Table<Level>,
  subRoofLevel: Level,
  multiGableRoofLevels: Table<RoofSystemLevel>,
): NodeBSP[] {
  const nodes: NodeBSP[] = [];

  if (!subRoofLevel) {
    return nodes;
  }

  for (const levelId in multiGableRoofLevels) {
    const level = levels[levelId];

    if (!level || level.floorIndex < subRoofLevel.floorIndex) {
      continue;
    }

    const { upperFloorRoomsBSP } = multiGableRoofLevels[levelId];
    if (!upperFloorRoomsBSP) {
      continue;
    }
    nodes.push(upperFloorRoomsBSP);
  }

  return nodes;
}

export function getUpperFloorsClippingNodes(
  levels: Table<Level>,
  subRoofLevel: Level,
  multiGableRoofLevels: Table<RoofSystemLevel>,
): ClippingNode[] {
  return getUpperFloorsRoomsBSPs(levels, subRoofLevel, multiGableRoofLevels).map((x) => clippingNodeNew(x));
}

export function collectRoofWallCuts(
  windows: Table<GeneratedWindowPlacement>,
  doors: Table<GeneratedDoorPlacement>,
  wallCuts: LevelHalfEdgeAssociationTable<WallCut>,
): WallOpening[] {
  const roofWallCuts: WallOpening[] = [];

  for (const windowId in windows) {
    const { opening } = windows[windowId];

    if (!opening) {
      continue;
    }

    roofWallCuts.push(opening);
  }

  for (const doorId in doors) {
    const { opening } = doors[doorId];

    if (!opening) {
      continue;
    }

    roofWallCuts.push(opening);
  }

  for (const levelId in wallCuts) {
    const levelWallCuts = wallCuts[levelId];
    for (const halfEdgeIndex in levelWallCuts) {
      const halfEdgeWallCuts = levelWallCuts[halfEdgeIndex];

      if (!halfEdgeWallCuts) {
        continue;
      }

      for (const wallCut of halfEdgeWallCuts) {
        const { opening } = wallCut.item;

        if (!opening) {
          continue;
        }

        roofWallCuts.push(opening);
      }
    }
  }

  return roofWallCuts;
}

export function buildSubRoofWallDiscardBSPs(
  level: Building["levels"],
  walls: Building["walls"],
  rooms: Building["rooms"],
  porchSettings: PorchSettings,
  rearPorchSettings: RearPorchSettings | undefined,
  targetedPorchSettings: TargetedPorchSettings,
  voidWallExtraOffset: number = 0,
): Table<NodeBSP> {
  const discardBSPs: RWTable<NodeBSP> = {};

  for (const levelId in level) {
    const discardBSP = buildSubRoofWallDiscardBSP(
      level[levelId],
      walls,
      rooms,
      porchSettings,
      rearPorchSettings,
      targetedPorchSettings,
      voidWallExtraOffset,
    );
    if (!discardBSP) {
      continue;
    }

    discardBSPs[levelId] = discardBSP;
  }

  return discardBSPs;
}

function buildSubRoofWallDiscardBSP(
  level: Level,
  walls: Building["walls"],
  rooms: Building["rooms"],
  porchSettings: PorchSettings,
  rearPorchSettings: RearPorchSettings | undefined,
  targetedPorchSettings: TargetedPorchSettings,
  voidWallExtraOffset: number = 0,
): NodeBSP | null {
  let discardBSP: NodeBSP | null = null;

  const rearPorchSettingsIfExists = rearPorchSettings ?? porchSettings;

  for (const roomId in rooms) {
    const room = rooms[roomId];
    const { type, level: roomLevel } = room;
    const { floorElevation, topOfWall, floorIndex } = roomLevel;

    if (floorIndex > level.floorIndex) {
      continue;
    }

    if (type !== RoomType.FRONT_PORCH && type !== RoomType.REAR_PORCH && type !== RoomType.PORTICO) {
      continue;
    }

    let topElevation = floorElevation + topOfWall;

    if (type === RoomType.PORTICO) {
      const { ceilingHeightOffset } = room;

      topElevation += ceilingHeightOffset;
    } else {
      const baseRoomId = getSourceRoomId(room);
      const roomPorchSettings = targetedPorchSettings[baseRoomId];
      const mergedPorchSettings = type === RoomType.FRONT_PORCH ? porchSettings : rearPorchSettingsIfExists;
      const resolvedPorchSettings = roomPorchSettings ?? mergedPorchSettings;
      const { topOfHeaderHeightOffset = 0 } = resolvedPorchSettings;

      topElevation += topOfHeaderHeightOffset;
    }

    const wallIndex = walls[roomLevel.id].wallIndex;

    const bottomPlane = planeNewXY(0, 0, floorElevation);
    const topPlane = planeNewXY(0, 0, topElevation);

    let perimeter: PolyLine2 | null = null;
    const offsets: number[] = [];

    const roomWalls = wallIndex.wallsInRoom(room);
    for (const wall of roomWalls) {
      if (!wall) {
        continue;
      }

      const { wallType } = wall;
      const path = getWallPath(wall);
      const { startX, startY, endX, endY, leftRoom } = path;

      const isLeftRoom = leftRoom === room;
      let edge = polyLine2ByTwoPoints(startX, startY, endX, endY);
      if (!isLeftRoom) {
        edge = polyLine2Reverse(edge);
      }

      perimeter = perimeter ? polyLine2Concat(perimeter, [edge[2], edge[3]]) : edge;

      switch (wallType) {
        case WallType.VOID:
          offsets.push((isLeftRoom ? wall.rightOffset : wall.leftOffset) + voidWallExtraOffset);
          break;
        case WallType.SOLID:
          offsets.push(isLeftRoom ? -wall.mass.leftOffset : -wall.mass.rightOffset);
          break;
        case WallType.SOLID_MASS:
          offsets.push(isLeftRoom ? -wall.leftOffset : -wall.rightOffset);
          break;
        default:
          offsets.push(0);
          assertUnreachable(wall);
      }
    }

    assertIsNotNullOrUndefined(perimeter);
    perimeter = offsetPolyLine2Variable(perimeter, offsets);
    perimeter = polyLine2RemoveInvalidVertices(perimeter);
    perimeter = polyLine2Simplify(perimeter);

    const discardEdgePolygons: PolygonBSP[] = [];
    for (let i = 0, l = perimeter.length - 2; i < l; i += 2) {
      const x1 = perimeter[i];
      const y1 = perimeter[i + 1];
      const z1 = floorElevation;

      const x2 = perimeter[i + 2];
      const y2 = perimeter[i + 3];
      const z2 = floorElevation;

      const x3 = x2;
      const y3 = y2;
      const z3 = topElevation;

      const x4 = x1;
      const y4 = y1;
      const z4 = topElevation;

      const edgePlane = planeBSPByThreePoints(x1, y1, z1, x2, y2, z2, x3, y3, z3);

      if (!edgePlane) {
        continue;
      }

      discardEdgePolygons.push(polygonBSPNew(0, [x1, y1, z1, x2, y2, z2, x3, y3, z3, x4, y4, z4], edgePlane));
    }

    perimeter = polyLine2SetCW(perimeter);
    const bottomFace = faceOrNullReverse(faceNew([polyLine2Copy(perimeter)], bottomPlane));
    const topFace = faceNew([polyLine2Copy(perimeter)], topPlane);

    if (bottomFace) {
      const discardCapPolygons: PolygonBSP[] = [];
      buildPolygonsFromFaces({
        faces: [bottomFace, topFace],
        polygons: discardCapPolygons,
      });

      discardBSP = addPolygonsToBSP(
        discardBSP,
        null,
        [...discardCapPolygons, ...discardEdgePolygons],
        MULTIGABLE_BSP_EPSILON,
      );
    }
  }

  return discardBSP;
}

export function buildWallOverlapBSPs(
  wallSystems: Building["walls"],
  blocks: Table<Block>,
  insideBSP: NodeBSP | null,
  includeHalfStories: boolean,
  offset: number = 0,
): NodeBSP[] {
  const wallOverlapBSPs: NodeBSP[] = [];

  const options: ClipFaceContext = {
    clippingNodes: [clippingNodeNew(insideBSP)],
    coplanarsToFront: true,
    epsilon: MULTIGABLE_BSP_EPSILON,
  };

  const collectFaces = (refFace: Face, faces: Face[], thickness: number) => {
    const { plane: refPlane } = refFace;
    const [refNormalX, refNormalY, refNormalZ] = planeNormal(refPlane);

    for (const face of faces) {
      const { loops, plane } = face;
      const [normalX, normalY, normalZ] = planeNormal(plane);

      // we filter out faces not parellel to the reference face (to avoid some extra side faces) and
      // invalid loops
      if (
        vec3Dot(refNormalX, refNormalY, refNormalZ, normalX, normalY, normalZ) < ONE_MINUS_DIST_EPSILON ||
        loops.length === 0 ||
        polyLine2Area(loops[0]) < DIST_EPSILON_SQUARED
      ) {
        continue;
      }

      if (!facesToClipByThickness[thickness]) {
        facesToClipByThickness[thickness] = [];
      }

      facesToClipByThickness[thickness].push(face);
    }
  };

  const facesToClipByThickness: { [thickness: number]: Face[] } = [];

  const addFaceToBSP = (face: Face, thickness: number) => {
    const { plane, loops } = face;

    const [nX, nY, nZ] = planeNormal(plane);
    const offsetLoop = offsetPolyLine2(loops[0], -offset);
    const pl3 = polyLine2OnPlane(plane, offsetLoop);

    const polygons: PolygonBSP[] = [];
    for (let i = 0, l = pl3.length - 3; i < l; i += 3) {
      const edgePolygon = buildEdgePolygon(
        pl3[i],
        pl3[i + 1],
        pl3[i + 2],
        pl3[i + 3],
        pl3[i + 4],
        pl3[i + 5],
        -nX * thickness,
        -nY * thickness,
        -nZ * thickness,
      );
      if (!edgePolygon) {
        continue;
      }
      polygons.push(edgePolygon);
    }

    const oppositePlane = planeMoveAlongNormal(planeCopyReversed(plane), thickness);

    buildPolygonsFromPolyline2(offsetLoop, plane, 0, polygons);
    buildPolygonsFromPolyline2(
      polyLine2Reverse(planeReProjectPolyLine2(offsetLoop, plane, oppositePlane)),
      oppositePlane,
      0,
      polygons,
    );
    const wallOverlapBSP = addPolygonsToBSP(null, null, polygons, MULTIGABLE_BSP_EPSILON);
    if (!wallOverlapBSP) {
      return;
    }

    wallOverlapBSPs.push(wallOverlapBSP);
  };

  const addFacesToBSP = (faces: Face[], thickness: number) => {
    let subFaces = faces;

    if (insideBSP) {
      subFaces = clipFaces({
        faces: subFaces,
        options,
      });
    }

    for (const subFace of subFaces) {
      if (!isValidFaceForBSPVolume(subFace, thickness, MULTIGABLE_BSP_EPSILON)) {
        continue;
      }

      addFaceToBSP(subFace, thickness);
    }
  };

  for (const { walls } of Object.values(wallSystems)) {
    for (const wallId in walls) {
      const wall = walls[wallId];

      if (wall.wallType !== WallType.SOLID) {
        continue;
      }

      if (!isExteriorWallCondition[wall.wallCondition]) {
        continue;
      }

      const { wallFaces, mass } = wall;
      const path = getWallPath(wall);
      const { leftRoom, rightRoom } = path;
      const { leftFace, rightFace } = mass;
      const { thickness } = solidWallSubstrateLayer(wall);

      if (thickness < DIST_EPSILON) {
        continue;
      }

      const leftBlock = leftRoom ? blocks[leftRoom?.parentId] : undefined;
      const rightBlock = rightRoom ? blocks[rightRoom?.parentId] : undefined;

      if (!includeHalfStories && (leftBlock?.isHalfStory || rightBlock?.isHalfStory)) {
        continue;
      }

      const { rightFaces, leftFaces } = wallFaces;

      if (leftRoom && isInteriorRoomEnclosure[leftRoom?.attributes.roomEnclosure]) {
        collectFaces(rightFace, rightFaces, thickness);
      }

      if (rightRoom && isInteriorRoomEnclosure[rightRoom?.attributes.roomEnclosure]) {
        collectFaces(leftFace, leftFaces, thickness);
      }
    }
  }

  // group coplanar wall faces by thickness

  // TODO: Should we generate this elsewhere for use in other downstream systems?

  for (const thicknessStr in facesToClipByThickness) {
    const thickness = parseFloat(thicknessStr);

    const coplanarFaceGroups = gatherOrientedCoplanarFaceGroupsFast(facesToClipByThickness[thicknessStr], 1e-3);
    const mergedFaces: Face[] = [];

    for (const coplanarFaceGroup of coplanarFaceGroups) {
      const coplanarFaces = mergeNonAdjacentCoplanarFaces(coplanarFaceGroup).map((face) => {
        const simplifiedLoop = polyLine2Simplify(face.loops[0]);
        return faceNew([simplifiedLoop], face.plane);
      });
      mergedFaces.push(...coplanarFaces);
    }

    for (const face of mergedFaces) {
      addFacesToBSP([face], thickness);
    }
  }

  return wallOverlapBSPs;
}
